// queryviz-api.js
import { API, Auth } from 'aws-amplify';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';

const API_NAME = 'OpenQueryAPI';

async function ensureAuthed() {
  // Ensures a valid Cognito session (refreshes if needed)
  await Auth.currentAuthenticatedUser();
}

/**
 * Return the QuickSight embed URL for a given query job id.
 * Usage: const url = await getQuicksightDashboardId(queryJobId);
 */
export async function getQuicksightDashboardId(queryJobId) {
  if (!queryJobId) throw new Error('queryJobId is required');
  await ensureAuthed();
  const path = `/queryviz/queryjobid/${encodeURIComponent(queryJobId)}`;
  const resp = await API.get(API_NAME, path);
  return resp?.embedUrl; // always return a string URL
}

/**
 * Return the QuickSight embed URL for a dashboard id (passes id token to backend).
 * Usage: const url = await getDashboardEmbedURL(dashboardId);
 */
export async function getDashboardEmbedURL(dashboardId) {
  if (!dashboardId) throw new Error('dashboardId is required');
  await ensureAuthed();
  const session = await Auth.currentSession();
  const idToken = session.getIdToken().getJwtToken();
  const path = `/queryviz/queryjobid/${encodeURIComponent(dashboardId)}`;
  const resp = await API.get(API_NAME, path, {
    queryStringParameters: { openIdToken: idToken },
  });
  return resp?.embedUrl; // always return a string URL
}

/**
 * Embed a QuickSight dashboard into #embeddingContainer
 * Usage: await initializeDashboardEmbed(urlStr, { contentOptions: {...} })
 */
export async function initializeDashboardEmbed(urlStr, config = {}) {
  if (!urlStr || typeof urlStr !== 'string') {
    throw new Error('initializeDashboardEmbed requires the embed URL string');
  }
  const embeddingContext = await createEmbeddingContext();
  const frameOptions = {
    url: urlStr,
    container: '#embeddingContainer',
  };
  const contentOptions = {
    locale: 'en-US',
    toolbarOptions: {
      export: true,
      reset: true,
      undoRedo: true,
      bookmarks: true,
    },
    ...(config.contentOptions || {}),
  };
  return await embeddingContext.embedDashboard(frameOptions, contentOptions);
}
